/**
 * Product Card JavaScript
 * Image slider and favorite functionality
 * Performance optimized, modular JS
 */

(function() {
  'use strict';

  class ProductCard {
    constructor(card) {
      this.card = card;
      this.slider = card.querySelector('[data-product-slider]');
      this.track = card.querySelector('[data-slider-track]');
      this.dots = card.querySelectorAll('[data-dot-index]');
      this.slides = card.querySelectorAll('[data-slide-index]');
      this.favoriteButton = card.querySelector('[data-favorite-button]');
      this.productId = card.dataset.productId;
      this.productHandle = card.dataset.productHandle; // Use handle for favorites
      this.currentIndex = 0;
      this.isDragging = false;
      this.startX = 0;
      this.currentX = 0;
      this.translateX = 0;
      this.prevTranslate = 0;
      this.animationID = null;
      
      this.init();
    }

    init() {
      if (this.slider && this.slides.length > 1) {
        this.setupSlider();
      }
      
      if (this.favoriteButton) {
        this.setupFavorite();
        this.loadFavoriteState();
      }
    }

    setupSlider() {
      this.setupDots();
      this.setupDrag();
      this.updateSliderPosition();
    }

    setupDots() {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index));
      });
    }

    setupDrag() {
      // Prevent link navigation when dragging
      const imageLink = this.card.querySelector('.product-card__image-link');
      if (imageLink) {
        imageLink.addEventListener('click', (e) => {
          if (this.isDragging) {
            e.preventDefault();
            e.stopPropagation();
          }
        });
      }
      
      // Mouse events
      this.slider.addEventListener('mousedown', this.handleStart.bind(this));
      document.addEventListener('mousemove', this.handleMove.bind(this));
      document.addEventListener('mouseup', this.handleEnd.bind(this));
      
      // Touch events
      this.slider.addEventListener('touchstart', this.handleStart.bind(this), { passive: true });
      this.slider.addEventListener('touchmove', this.handleMove.bind(this), { passive: false });
      this.slider.addEventListener('touchend', this.handleEnd.bind(this));
    }

    getPositionX(event) {
      return event.type.includes('mouse') ? event.clientX : event.touches[0].clientX;
    }

    handleStart(event) {
      this.isDragging = true;
      this.startX = this.getPositionX(event);
      this.track.classList.add('is-dragging');
      this.animationID = requestAnimationFrame(this.animation.bind(this));
    }

    handleMove(event) {
      if (!this.isDragging) return;
      
      event.preventDefault();
      this.currentX = this.getPositionX(event);
      this.translateX = this.currentX - this.startX;
    }

    handleEnd() {
      if (!this.isDragging) return;
      
      this.isDragging = false;
      this.track.classList.remove('is-dragging');
      cancelAnimationFrame(this.animationID);
      
      const movedBy = this.translateX;
      const threshold = 30;
      
      if (movedBy < -threshold && this.currentIndex < this.slides.length - 1) {
        this.nextSlide();
      } else if (movedBy > threshold && this.currentIndex > 0) {
        this.prevSlide();
      } else {
        this.updateSliderPosition();
      }
      
      this.translateX = 0;
      
      // Reset dragging flag after a short delay to prevent link clicks
      setTimeout(() => {
        this.isDragging = false;
      }, 100);
    }

    animation() {
      this.setSliderPosition();
      if (this.isDragging) {
        this.animationID = requestAnimationFrame(this.animation.bind(this));
      }
    }

    setSliderPosition() {
      const currentTranslate = this.prevTranslate + this.translateX;
      this.track.style.transform = `translateX(${currentTranslate}px)`;
    }

    updateSliderPosition() {
      if (!this.slides || this.slides.length === 0) return;
      const slideWidth = this.slides[0].offsetWidth;
      this.prevTranslate = -this.currentIndex * slideWidth;
      this.track.style.transform = `translateX(${this.prevTranslate}px)`;
      this.updateDots();
    }

    goToSlide(index) {
      if (index < 0 || index >= this.slides.length) return;
      
      this.currentIndex = index;
      this.updateSliderPosition();
    }

    nextSlide() {
      if (this.currentIndex < this.slides.length - 1) {
        this.currentIndex++;
        this.updateSliderPosition();
      }
    }

    prevSlide() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.updateSliderPosition();
      }
    }

    updateDots() {
      this.dots.forEach((dot, index) => {
        if (index === this.currentIndex) {
          dot.classList.add('product-card__dot--active');
        } else {
          dot.classList.remove('product-card__dot--active');
        }
      });
    }

    setupFavorite() {
      this.favoriteButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleFavorite();
      });
    }

    loadFavoriteState() {
      const favorites = this.getFavorites();
      // Use handle for consistency with customer-account.js
      if (this.productHandle && favorites.includes(this.productHandle)) {
        this.favoriteButton.classList.add('is-favorite');
      }
    }

    toggleFavorite() {
      if (!this.productHandle) return;
      
      const favorites = this.getFavorites();
      const index = favorites.indexOf(this.productHandle);
      
      if (index > -1) {
        favorites.splice(index, 1);
        this.favoriteButton.classList.remove('is-favorite');
      } else {
        favorites.push(this.productHandle);
        this.favoriteButton.classList.add('is-favorite');
      }
      
      this.saveFavorites(favorites);
    }

    getFavorites() {
      try {
        // Use customer_favorites to match customer-account.js
        const favorites = localStorage.getItem('customer_favorites');
        return favorites ? JSON.parse(favorites) : [];
      } catch (e) {
        return [];
      }
    }

    saveFavorites(favorites) {
      try {
        // Use customer_favorites to match customer-account.js
        localStorage.setItem('customer_favorites', JSON.stringify(favorites));
        
        // Dispatch custom event
        window.dispatchEvent(new CustomEvent('favorites:updated', {
          detail: { productHandle: this.productHandle, favorites: favorites }
        }));
      } catch (e) {
        console.error('Failed to save favorites:', e);
      }
    }
  }

  // Initialize product cards when DOM is ready
  function initProductCards() {
    const productCards = document.querySelectorAll('.product-card[data-product-id]');
    productCards.forEach(card => {
      new ProductCard(card);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductCards);
  } else {
    initProductCards();
  }

  // Re-initialize on dynamic content load
  if (typeof Shopify !== 'undefined') {
    document.addEventListener('shopify:section:load', initProductCards);
  }
})();

